import { useCallback, useEffect, useMemo, useState } from "react";
import type { Plan, Site } from "../lib/database.types";
import { useSites } from "../lib/sites";
import { apiFetch } from "../lib/api";
import type { Lang } from "../lib/prefs";
import { mxn } from "../lib/plans";

// M8 — Billing view (Facturación). Reads the caller's combined invoice from
// GET /api/billing (one source of truth) and renders: (1) the ONE combined
// monthly invoice with per-site line items summing to a total, mixed plans OK;
// (2) each Pro site's trial countdown; (3) a payment-method + RFC/CFDI section
// (stub — captured locally, no processor); (4) invoice history (empty on trial).
// The "Mejorar plan" affordances open the shared upgrade/checkout flow.

interface BillingLineItem {
  site_id: string;
  domain: string;
  plan: Plan;
  price_mxn: number;
  cadence: string;
  trial_ends_at: string | null;
  in_trial: boolean;
  due_mxn: number;
}

interface BillingResp {
  ok: boolean;
  currency: string;
  line_items: BillingLineItem[];
  total: number;
  due_now: number;
  invoices: unknown[];
}

const PAY_KEY = "citada.billing.pay";

const T = {
  es: {
    title: "Facturación",
    lead: "Una factura mensual combinada con todos tus sitios. Cobro por sitio, en MXN.",
    invoice: "Factura mensual combinada",
    site: "Sitio",
    plan: "Plan",
    cadence: "Cadencia",
    price: "Precio",
    total: "Total mensual",
    dueNow: "A cobrar este ciclo",
    weekly: "semanal",
    monthly: "mensual",
    planFree: "Gratis",
    planPro: "Pro",
    planEnt: "Enterprise",
    trialLeft: (n: number) => `Prueba · ${n} día${n === 1 ? "" : "s"} restantes`,
    trialUsed: "Prueba usada",
    ofDays: (u: number) => `${u} / 30 días`,
    manage: "Cambiar plan",
    upgrade: "Mejorar plan",
    noSites: "Aún no tienes sitios. Crea uno para ver tu factura.",
    payM: "Método de pago y CFDI",
    payNote: "Aceptamos tarjeta, SPEI y OXXO. Factura CFDI 4.0 automática.",
    stubNote: "Sin procesador conectado — los datos se guardan localmente (modo de prueba).",
    method: "Método",
    card: "Tarjeta",
    spei: "SPEI",
    oxxo: "OXXO",
    rfc: "RFC (para CFDI)",
    rfcPh: "XAXX010101000",
    save: "Guardar",
    saved: "Guardado",
    histT: "Historial",
    date: "Fecha",
    concept: "Concepto",
    cfdi: "CFDI",
    noInv: "Sin cargos todavía — estás en prueba gratuita.",
    loadErr: "No se pudo cargar la facturación.",
    dueTrialHint: "Los sitios Pro en prueba no se cobran hasta terminar la prueba.",
  },
  en: {
    title: "Billing",
    lead: "One combined monthly invoice across all your sites. Per-site pricing, in MXN.",
    invoice: "Combined monthly invoice",
    site: "Site",
    plan: "Plan",
    cadence: "Cadence",
    price: "Price",
    total: "Monthly total",
    dueNow: "Due this cycle",
    weekly: "weekly",
    monthly: "monthly",
    planFree: "Free",
    planPro: "Pro",
    planEnt: "Enterprise",
    trialLeft: (n: number) => `Trial · ${n} day${n === 1 ? "" : "s"} left`,
    trialUsed: "Trial used",
    ofDays: (u: number) => `${u} / 30 days`,
    manage: "Change plan",
    upgrade: "Upgrade",
    noSites: "You don't have any sites yet. Create one to see your invoice.",
    payM: "Payment method & CFDI",
    payNote: "Card, SPEI and OXXO accepted. Automatic CFDI 4.0 invoice.",
    stubNote: "No processor connected — details are stored locally (test mode).",
    method: "Method",
    card: "Card",
    spei: "SPEI",
    oxxo: "OXXO",
    rfc: "Tax ID (RFC, for CFDI)",
    rfcPh: "XAXX010101000",
    save: "Save",
    saved: "Saved",
    histT: "History",
    date: "Date",
    concept: "Concept",
    cfdi: "CFDI",
    noInv: "No charges yet — you are on the free trial.",
    loadErr: "Could not load billing.",
    dueTrialHint: "Pro sites in trial are not charged until the trial ends.",
  },
};

function planLabel(plan: Plan, t: (typeof T)["es"]): string {
  if (plan === "pro") return t.planPro;
  if (plan === "enterprise") return t.planEnt;
  return t.planFree;
}

function daysLeft(iso: string | null): number {
  if (!iso) return 0;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000));
}

type PayMethod = "card" | "spei" | "oxxo";

export default function Billing({
  lang,
  token,
  onUpgrade,
}: {
  lang: Lang;
  token: string;
  onUpgrade: (site: Site) => void;
}) {
  const t = T[lang];
  const { sites } = useSites();
  const [data, setData] = useState<BillingResp | null>(null);
  const [err, setErr] = useState(false);

  // Payment-method + RFC stub: captured and persisted locally, never sent to a
  // processor (there isn't one tonight).
  const [pay, setPay] = useState<PayMethod>("card");
  const [rfc, setRfc] = useState("");
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(PAY_KEY);
      if (raw) {
        const p = JSON.parse(raw) as { pay?: PayMethod; rfc?: string };
        if (p.pay) setPay(p.pay);
        if (p.rfc) setRfc(p.rfc);
      }
    } catch {
      /* ignore malformed local state */
    }
  }, []);

  // Refetch whenever the set of sites/plans changes (e.g. after an upgrade) so
  // the invoice stays in sync with the shared source of truth.
  const sig = useMemo(
    () => sites.map((s) => `${s.id}:${s.plan}`).join(","),
    [sites],
  );

  const load = useCallback(async () => {
    setErr(false);
    try {
      const res = await apiFetch<BillingResp>("/api/billing", token);
      setData(res);
    } catch {
      setErr(true);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load, sig]);

  function savePay() {
    try {
      localStorage.setItem(PAY_KEY, JSON.stringify({ pay, rfc }));
    } catch {
      /* localStorage may be unavailable; the stub still reflects the choice */
    }
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1600);
  }

  const items = data?.line_items ?? [];
  const siteById = useMemo(() => new Map(sites.map((s) => [s.id, s])), [sites]);
  const hasTrial = items.some((li) => li.in_trial);

  return (
    <>
      <div className="h-row">
        <h1>{t.title}</h1>
      </div>
      <p className="sub-line">{t.lead}</p>

      {err && <p className="auth-err">{t.loadErr}</p>}

      {/* Combined monthly invoice — per-site line items summing to a total. */}
      <div className="card mb">
        <div className="card-h">
          <h3>{t.invoice}</h3>
        </div>
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>{t.site}</th>
                <th>{t.plan}</th>
                <th>{t.cadence}</th>
                <th style={{ textAlign: "right" }}>{t.price}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    style={{ color: "var(--faint)", textAlign: "center", padding: 22 }}
                  >
                    {t.noSites}
                  </td>
                </tr>
              ) : (
                items.map((li) => {
                  const site = siteById.get(li.site_id);
                  const dleft = daysLeft(li.trial_ends_at);
                  const used = Math.min(30, Math.max(0, 30 - dleft));
                  return (
                    <tr key={li.site_id}>
                      <td>
                        <b>{li.domain}</b>
                        {li.in_trial && (
                          <div style={{ marginTop: 6, maxWidth: 200 }}>
                            <div
                              className="sub-line"
                              style={{ margin: "0 0 2px", fontSize: ".7rem" }}
                            >
                              {t.trialLeft(dleft)}
                            </div>
                            <div className="usebar">
                              <i style={{ width: `${(used / 30) * 100}%` }} />
                            </div>
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={`plan-tag ${li.plan}`}>{planLabel(li.plan, t)}</span>
                      </td>
                      <td style={{ color: "var(--muted)" }}>
                        {li.cadence === "weekly" ? t.weekly : t.monthly}
                      </td>
                      <td style={{ textAlign: "right", fontFamily: "var(--mono)" }}>
                        {li.in_trial && (
                          <span
                            style={{
                              color: "var(--faint)",
                              textDecoration: "line-through",
                              marginRight: 6,
                            }}
                          >
                            {mxn(li.price_mxn)}
                          </span>
                        )}
                        {li.in_trial ? mxn(0) : mxn(li.price_mxn)}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {site && (
                          <button
                            className="btn btn-ghost"
                            style={{ padding: "5px 10px", fontSize: ".78rem" }}
                            onClick={() => onUpgrade(site)}
                          >
                            {li.plan === "enterprise" ? t.manage : t.upgrade}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {items.length > 0 && data && (
              <tfoot>
                <tr>
                  <td colSpan={3} style={{ fontWeight: 600 }}>
                    {t.dueNow}
                  </td>
                  <td
                    style={{ textAlign: "right", fontFamily: "var(--mono)", fontWeight: 700 }}
                  >
                    {mxn(data.due_now)}
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td colSpan={3} style={{ color: "var(--muted)" }}>
                    {t.total}
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      fontFamily: "var(--mono)",
                      color: "var(--muted)",
                    }}
                  >
                    {mxn(data.total)} {data.currency}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        {hasTrial && (
          <p className="fn" style={{ margin: 0 }}>
            {t.dueTrialHint}
          </p>
        )}
      </div>

      {/* Payment method + RFC/CFDI (stub) and invoice history. */}
      <div className="grid2">
        <div className="card">
          <div className="card-h">
            <h3>{t.payM}</h3>
          </div>
          <div style={{ padding: "0 16px 16px" }}>
            <div className="fld">
              <label>{t.method}</label>
              <div className="paypick">
                {(["card", "spei", "oxxo"] as PayMethod[]).map((m) => (
                  <button
                    type="button"
                    key={m}
                    className={`payopt${pay === m ? " on" : ""}`}
                    onClick={() => setPay(m)}
                  >
                    {t[m]}
                  </button>
                ))}
              </div>
            </div>
            <div className="fld">
              <label>{t.rfc}</label>
              <input
                className="inp"
                value={rfc}
                onChange={(e) => setRfc(e.target.value.toUpperCase())}
                placeholder={t.rfcPh}
                autoComplete="off"
                spellCheck={false}
                maxLength={13}
              />
            </div>
            <p className="sub-line" style={{ margin: "12px 0 0" }}>
              {t.payNote}
            </p>
            <p className="sub-line" style={{ margin: "6px 0 0", color: "var(--faint)" }}>
              {t.stubNote}
            </p>
            <button
              className="btn btn-ghost btn-block"
              style={{ marginTop: 10 }}
              onClick={savePay}
            >
              {savedFlash ? t.saved : t.save}
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-h">
            <h3>{t.histT}</h3>
          </div>
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t.date}</th>
                  <th>{t.concept}</th>
                  <th>Total</th>
                  <th>{t.cfdi}</th>
                </tr>
              </thead>
              <tbody>
                {(data?.invoices.length ?? 0) === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      style={{ color: "var(--faint)", textAlign: "center", padding: 22 }}
                    >
                      {t.noInv}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
